import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  Badge,
  BadgeCreateRequest,
  BadgeDeleteResponse,
  BadgeEnableDisableResponse,
  BadgeListResponse,
  BadgeResponse,
  BadgeUpdateRequest,
} from '../models/badge.model';

@Injectable({
  providedIn: 'root',
})
export class BadgeService {
  private _allBadges: BehaviorSubject<BadgeListResponse | null> = new BehaviorSubject(null);
  private _badge: BehaviorSubject<Badge | null> = new BehaviorSubject(null);

  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get allBadges$(): Observable<BadgeListResponse> {
    return this._allBadges.asObservable();
  }

  get badge$(): Observable<Badge> {
    return this._badge.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all badges
   */
  getAll(categoryId?: string): Observable<BadgeListResponse> {
    let params = new HttpParams();
    if (categoryId) {
      params = params.set('category', categoryId);
    }

    return this._httpClient
      .get<BadgeListResponse>(environment.apiURL + 'badges/all', { params })
      .pipe(tap((response) => this._allBadges.next(response)));
  }

  /**
   * Get badges by category
   */
  getByCategory(categoryId: string): Observable<BadgeListResponse> {
    return this._httpClient
      .get<BadgeListResponse>(environment.apiURL + `badges/byCategory/${categoryId}`)
      .pipe(tap((response) => this._allBadges.next(response)));
  }

  /**
   * Get badge by ID
   */
  getById(id: string): Observable<BadgeResponse> {
    return this._httpClient
      .get<BadgeResponse>(environment.apiURL + `badges/${id}`)
      .pipe(tap((response) => this._badge.next(response.badge)));
  }

  /**
   * Create badge
   */
  create(data: BadgeCreateRequest): Observable<BadgeResponse> {
    return this.allBadges$.pipe(
      take(1),
      switchMap((badges) =>
        this._httpClient.post<BadgeResponse>(environment.apiURL + 'badges/add', data).pipe(
          map((response) => {
            if (badges && badges.badges) {
              badges.badges.unshift(response.badge);
              this._allBadges.next(badges);
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Update badge
   */
  update(id: string, data: BadgeUpdateRequest): Observable<BadgeResponse> {
    return this.allBadges$.pipe(
      take(1),
      switchMap((badges) =>
        this._httpClient.put<BadgeResponse>(environment.apiURL + `badges/edit/${id}`, data).pipe(
          map((response) => {
            if (badges && badges.badges) {
              const index = badges.badges.findIndex((badge) => badge._id === id);
              if (index !== -1) {
                badges.badges[index] = response.badge;
                this._allBadges.next(badges);
              }
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Enable/Disable badge
   */
  enableDisable(id: string): Observable<BadgeEnableDisableResponse> {
    return this.allBadges$.pipe(
      take(1),
      switchMap((badges) =>
        this._httpClient.get<BadgeEnableDisableResponse>(environment.apiURL + `badges/enableDisable/${id}`).pipe(
          map((response) => {
            if (badges && badges.badges) {
              const index = badges.badges.findIndex((badge) => badge._id === id);
              if (index !== -1) {
                badges.badges[index].isDisabled = response.isDisabled;
                this._allBadges.next(badges);
              }
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Delete badge
   */
  delete(id: string): Observable<BadgeDeleteResponse> {
    return this.allBadges$.pipe(
      take(1),
      switchMap((badges) =>
        this._httpClient.delete<BadgeDeleteResponse>(environment.apiURL + `badges/delete/${id}`).pipe(
          map((response) => {
            if (badges && badges.badges) {
              const index = badges.badges.findIndex((badge) => badge._id === id);
              if (index !== -1) {
                badges.badges.splice(index, 1);
                this._allBadges.next(badges);
              }
            }
            return response;
          })
        )
      )
    );
  }
}
